import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

type SystemMenuWithChildrenResponse = Partial<{
  id: (number | null);
  label: (string | null);
  to_url: (string | null);
  parent_id: (number | null);
  order_num: (number | null);
  component: (string | null);
  icon: (string | null);
  has_menu: (boolean | null);
  c_at: (string | null);
  c_by: (number | null);
  m_at: (string | null);
  m_by: (number | null);
  children: Array<SystemMenuWithChildrenResponse>;
}>;

const LoginRequest = z
  .object({
    user_name: z.string().min(3).max(100),
    password: z.string().min(8).max(128),
  })
  .passthrough();
const PosLoc = z
  .object({
    loc_code: z.union([z.string(), z.null()]),
    loc_desc: z.union([z.string(), z.null()]),
    loc_address: z.union([z.string(), z.null()]),
    loc_active: z.union([z.boolean(), z.null()]),
    loc_remote: z.union([z.boolean(), z.null()]),
  })
  .partial()
  .passthrough();
const ItUserGroup = z
  .object({
    id: z.union([z.number(), z.null()]),
    group_name: z.union([z.string(), z.null()]),
    level: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const ItInstitute = z
  .object({
    id: z.union([z.number(), z.null()]),
    name_ln1: z.union([z.string(), z.null()]),
    name_ln2: z.union([z.string(), z.null()]),
    name_ln3: z.union([z.string(), z.null()]),
    address_ln1: z.union([z.string(), z.null()]),
    address_ln2: z.union([z.string(), z.null()]),
    address_ln3: z.union([z.string(), z.null()]),
    email: z.union([z.string(), z.null()]),
    phone: z.union([z.string(), z.null()]),
    reg_num: z.union([z.string(), z.null()]),
    reg_date: z.union([z.string(), z.null()]),
    sms_mask: z.union([z.string(), z.null()]),
    sms_mask_id: z.union([z.string(), z.null()]),
    sms_active: z.union([z.number(), z.null()]).default(0),
    swift_code: z.union([z.string(), z.null()]),
    coop_fund: z.union([z.number(), z.null()]),
    statu_reserves: z.union([z.number(), z.null()]),
    profit: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
    age_min: z.union([z.number(), z.null()]),
    age_max: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const LoginResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.string(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([PosLoc, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    access_token: z.string(),
    refresh_token: z.string(),
  })
  .passthrough();
const APIResponse_LoginResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([LoginResponse, z.null()]),
  })
  .partial()
  .passthrough();
const ValidationError = z
  .object({
    loc: z.array(z.union([z.string(), z.number()])),
    msg: z.string(),
    type: z.string(),
  })
  .passthrough();
const HTTPValidationError = z
  .object({ detail: z.array(ValidationError) })
  .partial()
  .passthrough();
const APIResponse = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.unknown(), z.null()]),
  })
  .partial()
  .passthrough();
const RefreshTokenResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.string(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([PosLoc, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    access_token: z.string(),
  })
  .passthrough();
const APIResponse_RefreshTokenResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([RefreshTokenResponse, z.null()]),
  })
  .partial()
  .passthrough();
const FirstTimeLoginRequest = z
  .object({
    user_id: z.number().int(),
    user_name: z.string().min(3).max(100),
    updated_user_name: z.union([z.string(), z.null()]).optional(),
    password: z.string().min(8).max(128),
    token: z.string(),
  })
  .passthrough();
const AuthResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.string(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([PosLoc, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_AuthResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([AuthResponse, z.null()]),
  })
  .partial()
  .passthrough();
const UserWithActiveStatusResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.string(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([PosLoc, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    password_status: z.union([z.number(), z.null()]),
    status: z.union([z.number(), z.null()]),
    online_status: z.boolean(),
    reset_password_url: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_UserWithActiveStatusResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserWithActiveStatusResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UserPermissionResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    permission: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_UserPermissionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserPermissionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UserMenuResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    label: z.union([z.string(), z.null()]),
    to_url: z.union([z.string(), z.null()]),
    parent_id: z.union([z.number(), z.null()]),
    order_num: z.union([z.number(), z.null()]),
    component: z.union([z.string(), z.null()]),
    icon: z.union([z.string(), z.null()]),
    has_menu: z.union([z.boolean(), z.null()]),
    cashier_required: z.union([z.boolean(), z.null()]),
    c_at: z.union([z.string(), z.null()]),
    c_by: z.union([z.number(), z.null()]),
    m_at: z.union([z.string(), z.null()]),
    m_by: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_UserMenuResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserMenuResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UserGroupsResponse = z
  .object({ id: z.number().int(), name: z.string() })
  .passthrough();
const APIResponse_list_UserGroupsResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(UserGroupsResponse), z.null()]),
  })
  .partial()
  .passthrough();
const UpdateFavoriteMenuRequest = z
  .object({ menu_id: z.number().int(), favourite: z.boolean() })
  .passthrough();
const UpdateFavouriteMenuResponse = z
  .object({ menu_id: z.number().int(), favourite: z.boolean() })
  .passthrough();
const APIResponse_UpdateFavouriteMenuResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([UpdateFavouriteMenuResponse, z.null()]),
  })
  .partial()
  .passthrough();
const UserRole = z.enum(['ADMIN', 'CLERK', 'CASHIER', 'SALES_REP', 'CUSTOMER', 'RIDER']);
const UserCreateRequest = z
  .object({
    name: z.string().min(1).max(300),
    user_name: z.string().min(3).max(100),
    ref_number: z.union([z.string(), z.null()]).optional(),
    user_group_id: z.union([z.number(), z.null()]).optional(),
    customer_id: z.union([z.number(), z.null()]).optional(),
    active_branch_id: z.union([z.string(), z.null()]).optional(),
    user_role: z.union([UserRole, z.null()]).optional().default('CLERK'),
    system_permissions: z
      .union([z.array(z.number().int()), z.null()])
      .optional(),
    system_menus: z.union([z.array(z.number().int()), z.null()]).optional(),
    user_reports: z.union([z.array(z.number().int()), z.null()]).optional(),
  })
  .passthrough();
const UserCreateResponse = z
  .object({
    id: z.number().int(),
    user_id: z.number().int(),
    user_name: z.string(),
    name: z.string(),
    user_role: z.union([z.string(), z.null()]).optional(),
    active_branch_id: z.union([z.string(), z.null()]),
    user_group_id: z.union([z.number(), z.null()]),
    active_branch: z.union([PosLoc, z.null()]),
    user_group: z.union([ItUserGroup, z.null()]),
    institute: z.union([ItInstitute, z.null()]),
    ref_number: z.union([z.string(), z.null()]).optional(),
    reset_password_url: z.union([z.string(), z.null()]),
  })
  .passthrough();
const APIResponse_UserCreateResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([UserCreateResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_int__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(z.number().int()), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_str_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_bool_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.boolean(), z.null()]),
  })
  .partial()
  .passthrough();
const SystemPermissionResponse = z
  .object({ id: z.number().int(), name: z.string(), description: z.string() })
  .passthrough();
const APIResponse_list_SystemPermissionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SystemPermissionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosItemUpdateRequest = z
  .object({
    item_instituteid: z.union([z.number(), z.null()]),
    item_crdate: z.union([z.string(), z.null()]),
    item_crby: z.union([z.string(), z.null()]),
    item_mddate: z.union([z.string(), z.null()]),
    item_mdby: z.union([z.string(), z.null()]),
    item_code: z.union([z.string(), z.null()]),
    item_autocode: z.union([z.string(), z.null()]),
    item_name: z.union([z.string(), z.null()]),
    item_webname: z.union([z.string(), z.null()]),
    item_group1: z.union([z.string(), z.null()]),
    item_group2: z.union([z.string(), z.null()]),
    item_group3: z.union([z.string(), z.null()]),
    item_uom: z.union([z.string(), z.null()]),
    item_case: z.union([z.number(), z.string(), z.null()]),
    item_selling: z.union([z.number(), z.string(), z.null()]),
    item_onlselling: z.union([z.number(), z.string(), z.null()]),
    item_cost: z.union([z.number(), z.string(), z.null()]),
    item_mrp: z.union([z.number(), z.string(), z.null()]),
    item_avgcost: z.union([z.number(), z.string(), z.null()]),
    item_gp: z.union([z.number(), z.string(), z.null()]),
    item_fixedgp: z.union([z.number(), z.string(), z.null()]),
    item_type: z.union([z.string(), z.null()]),
    item_reordermin: z.union([z.number(), z.string(), z.null()]),
    item_reordermax: z.union([z.number(), z.string(), z.null()]),
    item_enablereorderalert: z.union([z.boolean(), z.null()]),
    item_lastpurchaseprice: z.union([z.number(), z.null()]),
    item_shortdescription: z.union([z.string(), z.null()]),
    item_barcode: z.union([z.string(), z.null()]),
    item_qrcode: z.union([z.string(), z.null()]),
    item_positem: z.union([z.boolean(), z.null()]),
    item_dirgrn: z.union([z.boolean(), z.null()]),
    item_hoprice: z.union([z.boolean(), z.null()]),
    item_vendorplu: z.union([z.string(), z.null()]),
    item_active: z.union([z.boolean(), z.null()]),
    item_generagebatch: z.union([z.boolean(), z.null()]),
    item_allowstcode: z.union([z.boolean(), z.null()]),
    item_calreceipe: z.union([z.boolean(), z.null()]),
    item_rawitem: z.union([z.boolean(), z.null()]),
    item_activedis: z.union([z.boolean(), z.null()]),
    item_weighted: z.union([z.boolean(), z.null()]),
    item_weight_kg: z.union([z.number(), z.null()]),
    item_courier_allowed: z.union([z.boolean(), z.null()]),
    is_returnable: z.union([z.boolean(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_dict_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.object({}).partial().passthrough(), z.null()]),
  })
  .partial()
  .passthrough();
const search = z.union([z.string(), z.null()]).optional();
const PosItemResponse = z
  .object({
    item_id: z.union([z.number(), z.null()]),
    item_code: z.union([z.string(), z.null()]),
    item_name: z.union([z.string(), z.null()]),
    item_group1: z.union([z.string(), z.null()]),
    item_uom: z.union([z.string(), z.null()]),
    item_case: z.union([z.string(), z.number(), z.number(), z.null()]),
    item_selling: z.union([z.number(), z.null()]),
    item_onlselling: z.union([z.number(), z.null()]),
    item_cost: z.union([z.number(), z.null()]),
    item_mrp: z.union([z.number(), z.null()]),
    item_avgcost: z.union([z.number(), z.null()]),
    item_gp: z.union([z.number(), z.null()]),
    item_active: z.union([z.number(), z.null()]),
    item_webname: z.union([z.string(), z.null()]),
    item_shortdescription: z.union([z.string(), z.null()]),
    item_barcode: z.union([z.string(), z.null()]),
    item_qrcode: z.union([z.string(), z.null()]),
    item_vendorplu: z.union([z.string(), z.null()]),
    item_reordermin: z.union([z.number(), z.null()]),
    item_reordermax: z.union([z.number(), z.null()]),
    item_enablereorderalert: z.union([z.boolean(), z.null()]),
    item_positem: z.union([z.boolean(), z.null()]),
    item_dirgrn: z.union([z.boolean(), z.null()]),
    item_hoprice: z.union([z.boolean(), z.null()]),
    item_generagebatch: z.union([z.boolean(), z.null()]),
    item_allowstcode: z.union([z.boolean(), z.null()]),
    item_calreceipe: z.union([z.boolean(), z.null()]),
    item_rawitem: z.union([z.boolean(), z.null()]),
    item_activedis: z.union([z.boolean(), z.null()]),
    item_weighted: z.union([z.boolean(), z.null()]),
    item_weight_kg: z.union([z.number(), z.null()]),
    item_courier_allowed: z.union([z.boolean(), z.null()]),
    is_returnable: z.union([z.boolean(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_PosItemResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosItemResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosCustomerUpdateRequest = z
  .object({
    cus_accno: z.union([z.string(), z.null()]),
    cus_code: z.union([z.string(), z.null()]),
    cus_name: z.union([z.string(), z.null()]),
    cus_title: z.union([z.string(), z.null()]),
    cus_crdate: z.union([z.string(), z.null()]),
    cus_crby: z.union([z.string(), z.null()]),
    cus_mddate: z.union([z.string(), z.null()]),
    cus_mdby: z.union([z.string(), z.null()]),
    cus_groupid: z.union([z.number(), z.null()]),
    cus_active: z.union([z.boolean(), z.null()]),
    cus_add1: z.union([z.string(), z.null()]),
    cus_add2: z.union([z.string(), z.null()]),
    cus_add3: z.union([z.string(), z.null()]),
    cus_add4: z.union([z.string(), z.null()]),
    cus_tinno: z.union([z.string(), z.null()]),
    cus_district: z.union([z.string(), z.null()]),
    cus_dsdivision: z.union([z.string(), z.null()]),
    cus_gndivision: z.union([z.string(), z.null()]),
    cus_tep1: z.union([z.string(), z.null()]),
    cus_tep2: z.union([z.string(), z.null()]),
    cus_email: z.union([z.string(), z.null()]),
    cus_dob: z.union([z.string(), z.null()]),
    cus_prof: z.union([z.string(), z.null()]),
    cus_spouse: z.union([z.string(), z.null()]),
    cus_creditlimit: z.union([z.number(), z.string(), z.null()]),
    cus_creditperiod: z.union([z.number(), z.null()]),
    cus_opbal: z.union([z.number(), z.string(), z.null()]),
    cus_purchase: z.union([z.number(), z.string(), z.null()]),
    cus_returns: z.union([z.number(), z.string(), z.null()]),
    cus_creditnote: z.union([z.number(), z.string(), z.null()]),
    cus_debitnote: z.union([z.number(), z.string(), z.null()]),
    cus_payment: z.union([z.number(), z.string(), z.null()]),
    cus_balance: z.union([z.number(), z.string(), z.null()]),
    cus_loyaltyactive: z.union([z.boolean(), z.null()]),
    cus_openpoint: z.union([z.number(), z.string(), z.null()]),
    cus_earnepoint: z.union([z.number(), z.string(), z.null()]),
    cus_redeempoint: z.union([z.number(), z.string(), z.null()]),
    cus_points: z.union([z.number(), z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PosCustomerResponse = z
  .object({
    cus_id: z.union([z.number(), z.null()]),
    cus_accno: z.union([z.string(), z.null()]),
    cus_code: z.union([z.string(), z.null()]),
    cus_name: z.union([z.string(), z.null()]),
    cus_title: z.union([z.string(), z.null()]),
    cus_crdate: z.union([z.string(), z.null()]),
    cus_crby: z.union([z.string(), z.null()]),
    cus_mddate: z.union([z.string(), z.null()]),
    cus_mdby: z.union([z.string(), z.null()]),
    cus_groupid: z.union([z.number(), z.null()]),
    cus_active: z.union([z.number(), z.boolean(), z.null()]),
    cus_add1: z.union([z.string(), z.null()]),
    cus_add2: z.union([z.string(), z.null()]),
    cus_add3: z.union([z.string(), z.null()]),
    cus_add4: z.union([z.string(), z.null()]),
    cus_tinno: z.union([z.string(), z.null()]),
    cus_district: z.union([z.string(), z.null()]),
    cus_dsdivision: z.union([z.string(), z.null()]),
    cus_gndivision: z.union([z.string(), z.null()]),
    cus_tep1: z.union([z.string(), z.null()]),
    cus_tep2: z.union([z.string(), z.null()]),
    cus_email: z.union([z.string(), z.null()]),
    cus_dob: z.union([z.string(), z.null()]),
    cus_prof: z.union([z.string(), z.null()]),
    cus_spouse: z.union([z.string(), z.null()]),
    cus_creditlimit: z.union([z.number(), z.null()]),
    cus_creditperiod: z.union([z.number(), z.null()]),
    cus_opbal: z.union([z.number(), z.null()]),
    cus_purchase: z.union([z.number(), z.null()]),
    cus_returns: z.union([z.number(), z.null()]),
    cus_creditnote: z.union([z.number(), z.null()]),
    cus_debitnote: z.union([z.number(), z.null()]),
    cus_payment: z.union([z.number(), z.null()]),
    cus_balance: z.union([z.number(), z.null()]),
    cus_loyaltyactive: z.union([z.number(), z.boolean(), z.null()]),
    cus_openpoint: z.union([z.number(), z.null()]),
    cus_earnepoint: z.union([z.number(), z.null()]),
    cus_redeempoint: z.union([z.number(), z.null()]),
    cus_points: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_PosCustomerResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosCustomerResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosVendorUpdateRequest = z
  .object({
    ven_accno: z.union([z.string(), z.null()]),
    ven_code: z.union([z.string(), z.null()]),
    ven_Name: z.union([z.string(), z.null()]),
    ven_titile: z.union([z.string(), z.null()]),
    ven_crdate: z.union([z.string(), z.null()]),
    ven_crby: z.union([z.string(), z.null()]),
    ven_mddate: z.union([z.string(), z.null()]),
    ven_mdby: z.union([z.string(), z.null()]),
    ven_groupid: z.union([z.string(), z.null()]),
    ven_active: z.union([z.boolean(), z.null()]),
    ven_add1: z.union([z.string(), z.null()]),
    ven_add2: z.union([z.string(), z.null()]),
    ven_add3: z.union([z.string(), z.null()]),
    ven_add4: z.union([z.string(), z.null()]),
    ven_tep1: z.union([z.string(), z.null()]),
    ven_tep2: z.union([z.string(), z.null()]),
    ven_email: z.union([z.string(), z.null()]),
    ven_contact: z.union([z.string(), z.null()]),
    ven_reordercycle: z.union([z.number(), z.null()]),
    ven_creditlimit: z.union([z.number(), z.string(), z.null()]),
    ven_creditperiod: z.union([z.number(), z.null()]),
    ven_opbal: z.union([z.number(), z.string(), z.null()]),
    ven_purchase: z.union([z.number(), z.string(), z.null()]),
    ven_returns: z.union([z.number(), z.string(), z.null()]),
    ven_creditnote: z.union([z.number(), z.string(), z.null()]),
    ven_debitnote: z.union([z.number(), z.string(), z.null()]),
    ven_payment: z.union([z.number(), z.string(), z.null()]),
    ven_balance: z.union([z.number(), z.string(), z.null()]),
    ven_taxregno: z.union([z.string(), z.null()]),
    ven_latitude: z.union([z.string(), z.null()]),
    ven_longitude: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PosVendorResponse = z
  .object({
    ven_id: z.union([z.number(), z.null()]),
    ven_accno: z.union([z.string(), z.null()]),
    ven_code: z.union([z.string(), z.null()]),
    ven_Name: z.union([z.string(), z.null()]),
    ven_titile: z.union([z.string(), z.null()]),
    ven_crdate: z.union([z.string(), z.null()]),
    ven_crby: z.union([z.string(), z.null()]),
    ven_mddate: z.union([z.string(), z.null()]),
    ven_mdby: z.union([z.string(), z.null()]),
    ven_groupid: z.union([z.string(), z.null()]),
    ven_active: z.union([z.number(), z.boolean(), z.null()]),
    ven_add1: z.union([z.string(), z.null()]),
    ven_add2: z.union([z.string(), z.null()]),
    ven_add3: z.union([z.string(), z.null()]),
    ven_add4: z.union([z.string(), z.null()]),
    ven_tep1: z.union([z.string(), z.null()]),
    ven_tep2: z.union([z.string(), z.null()]),
    ven_email: z.union([z.string(), z.null()]),
    ven_contact: z.union([z.string(), z.null()]),
    ven_reordercycle: z.union([z.number(), z.null()]),
    ven_creditlimit: z.union([z.number(), z.null()]),
    ven_creditperiod: z.union([z.number(), z.null()]),
    ven_opbal: z.union([z.number(), z.null()]),
    ven_purchase: z.union([z.number(), z.null()]),
    ven_returns: z.union([z.number(), z.null()]),
    ven_creditnote: z.union([z.number(), z.null()]),
    ven_debitnote: z.union([z.number(), z.null()]),
    ven_payment: z.union([z.number(), z.null()]),
    ven_balance: z.union([z.number(), z.null()]),
    ven_taxregno: z.union([z.string(), z.null()]),
    ven_latitude: z.union([z.string(), z.null()]),
    ven_longitude: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_PosVendorResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosVendorResponse), z.null()]),
  })
  .partial()
  .passthrough();
const floatamt = z.union([z.number(), z.null()]).optional();
const SignonUpdateRequest = z
  .object({
    user_name: z.union([z.string(), z.null()]),
    userName: z.union([z.string(), z.null()]),
    signon: z.union([z.number(), z.null()]),
    signondate: z.union([z.string(), z.null()]),
    signontime: z.union([z.string(), z.null()]),
    station_id: z.union([z.string(), z.null()]),
    stationid: z.union([z.string(), z.null()]),
    floatamt: z.union([z.number(), z.null()]),
  })
  .partial()
  .passthrough();
const update_user_signon_records_api_api_signon_user_records__put_Body =
  z.union([SignonUpdateRequest, z.null()]);
const SignoffRequest = z
  .object({
    user_name: z.union([z.string(), z.null()]),
    userName: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const signoff_user_api_api_signon_signoff__put_Body = z.union([
  SignoffRequest,
  z.null(),
]);
const PosCashRecWithCreate = z
  .object({
    payment_loc: z.union([z.string(), z.null()]),
    payment_date: z.union([z.string(), z.null()]),
    payment_time: z.union([z.string(), z.null()]),
    payment_cashier_id: z.union([z.string(), z.null()]),
    payment_station: z.union([z.number(), z.string(), z.null()]),
    payment_shift: z.union([z.number(), z.null()]),
    payment_description: z.union([z.string(), z.null()]),
    payment_amount: z.union([z.number(), z.string(), z.null()]),
    payment_mode: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PosCashRecWithResponse = z
  .object({
    payment_no: z.number().int(),
    payment_loc: z.union([z.string(), z.null()]).optional(),
    payment_date: z.union([z.string(), z.null()]).optional(),
    payment_time: z.union([z.string(), z.null()]).optional(),
    payment_cashier_id: z.union([z.string(), z.null()]).optional(),
    payment_station: z.union([z.number(), z.null()]).optional(),
    payment_shift: z.union([z.number(), z.null()]).optional(),
    payment_description: z.union([z.string(), z.null()]).optional(),
    payment_amount: z.union([z.number(), z.null()]).optional(),
    payment_mode: z.union([z.string(), z.null()]).optional(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .passthrough();
const APIResponse_List_PosCashRecWithResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosCashRecWithResponse), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_PosCashRecWithResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PosCashRecWithResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PosCashRecWithUpdate = z
  .object({
    payment_loc: z.union([z.string(), z.null()]),
    payment_date: z.union([z.string(), z.null()]),
    payment_time: z.union([z.string(), z.null()]),
    payment_cashier_id: z.union([z.string(), z.null()]),
    payment_station: z.union([z.number(), z.string(), z.null()]),
    payment_shift: z.union([z.number(), z.null()]),
    payment_description: z.union([z.string(), z.null()]),
    payment_amount: z.union([z.number(), z.string(), z.null()]),
    payment_mode: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PosItemGroupCreate = z
  .object({
    group_code: z.string(),
    group_name: z.string(),
    group_icon: z.union([z.string(), z.null()]).optional(),
    group_image: z.union([z.string(), z.null()]).optional(),
    group_saccno: z.union([z.string(), z.null()]).optional(),
    group_caccno: z.union([z.string(), z.null()]).optional(),
    group_iaccno: z.union([z.string(), z.null()]).optional(),
    group_prchadays: z.union([z.number(), z.null()]).optional(),
    group_reorder: z.union([z.number(), z.null()]).optional(),
    group_crby: z.union([z.string(), z.null()]).optional(),
    group_mdat: z.union([z.string(), z.null()]).optional(),
    group_mdby: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosItemGroupResponse = z
  .object({
    group_code: z.union([z.string(), z.null()]),
    group_name: z.union([z.string(), z.null()]),
    group_icon: z.union([z.string(), z.null()]),
    group_image: z.union([z.string(), z.null()]),
    group_saccno: z.union([z.string(), z.null()]),
    group_caccno: z.union([z.string(), z.null()]),
    group_iaccno: z.union([z.string(), z.null()]),
    group_prchadays: z.union([z.number(), z.null()]),
    group_reorder: z.union([z.number(), z.null()]),
    group_id: z.union([z.number(), z.null()]),
    group_crat: z.union([z.string(), z.null()]),
    group_crby: z.union([z.string(), z.null()]),
    group_mdat: z.union([z.string(), z.null()]),
    group_mdby: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_PosItemGroupResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PosItemGroupResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_PosItemGroupResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosItemGroupResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosItemGroupUpdate = z
  .object({
    group_code: z.string(),
    group_name: z.string(),
    group_icon: z.union([z.string(), z.null()]).optional(),
    group_image: z.union([z.string(), z.null()]).optional(),
    group_saccno: z.union([z.string(), z.null()]).optional(),
    group_caccno: z.union([z.string(), z.null()]).optional(),
    group_iaccno: z.union([z.string(), z.null()]).optional(),
    group_prchadays: z.union([z.number(), z.null()]).optional(),
    group_reorder: z.union([z.number(), z.null()]).optional(),
    group_mdat: z.union([z.string(), z.null()]).optional(),
    group_mdby: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosGLAccountCreate = z
  .object({
    acc_code: z.string(),
    acc_name: z.string(),
    acc_branch_id: z.string(),
    status: z.union([z.number(), z.null()]).optional().default(1),
  })
  .passthrough();
const PosGLAccountResponse = z
  .object({
    acc_code: z.union([z.string(), z.null()]),
    acc_name: z.union([z.string(), z.null()]),
    acc_branch_id: z.union([z.string(), z.null()]),
    status: z.union([z.number(), z.null()]),
    id: z.union([z.number(), z.null()]),
    acc_crat: z.union([z.string(), z.null()]),
    acc_crby: z.union([z.string(), z.null()]),
    acc_mdat: z.union([z.string(), z.null()]),
    acc_mdby: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_PosGLAccountResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PosGLAccountResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PosGLAccountResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosGLAccountResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosGLAccountSuggestionResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    acc_code: z.union([z.string(), z.null()]),
    acc_name: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_list_PosGLAccountSuggestionResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosGLAccountSuggestionResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosGLAccountUpdate = z
  .object({
    acc_code: z.string(),
    acc_name: z.string(),
    acc_branch_id: z.string(),
    status: z.union([z.number(), z.null()]).optional().default(1),
  })
  .passthrough();
const APIResponse_List_Dict_str__Any___ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(z.object({}).partial().passthrough()), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_Dict_str__Any__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.object({}).partial().passthrough(), z.null()]),
  })
  .partial()
  .passthrough();
const PosItemLotsResponse = z
  .object({
    itemlots_code: z.string(),
    itemlots_lot: z.union([z.string(), z.null()]).optional(),
    itemlots_loc: z.union([z.string(), z.null()]).optional(),
    itemlots_desc: z.union([z.string(), z.null()]).optional(),
    itemlots_selling: z.union([z.number(), z.null()]).optional(),
    itemlots_cost: z.union([z.number(), z.null()]).optional(),
    itemlots_active: z.union([z.boolean(), z.null()]).optional(),
    itemlots_barcode: z.union([z.string(), z.null()]).optional(),
    itemlots_group: z.union([z.string(), z.null()]).optional(),
    itemlots_sih: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_List_PosItemLotsResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosItemLotsResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosPayModeResponse = z
  .object({
    pay_code: z.string(),
    pay_typedesc: z.union([z.string(), z.null()]).optional(),
    pay_iscredit: z.union([z.boolean(), z.null()]).optional(),
    pay_iscash: z.union([z.boolean(), z.null()]).optional(),
    pay_accno: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_List_PosPayModeResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosPayModeResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosPayModeDetResponse = z
  .object({
    paycode: z.string(),
    paystno: z.string(),
    payDispno: z.number().int().optional().default(0),
  })
  .passthrough();
const APIResponse_List_PosPayModeDetResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosPayModeDetResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosRecentOrderItemResponse = z
  .object({
    item_id: z.union([z.number(), z.null()]).optional(),
    item_code: z.union([z.string(), z.null()]).optional(),
    item_name: z.union([z.string(), z.null()]).optional(),
    quantity: z.union([z.number(), z.null()]),
    unit_price: z.union([z.number(), z.null()]),
    original_price: z.union([z.number(), z.null()]).optional(),
    discount: z.union([z.number(), z.null()]).optional(),
    amount: z.union([z.number(), z.null()]),
  })
  .passthrough();
const PosRecentOrderResponse = z
  .object({
    order_no: z.string(),
    item_count: z.number().int(),
    amount: z.union([z.number(), z.null()]),
    created_at: z.union([z.string(), z.null()]).optional(),
    member: z.union([z.string(), z.null()]).optional(),
    rep: z.union([z.string(), z.null()]).optional(),
    items: z.array(PosRecentOrderItemResponse).optional(),
  })
  .passthrough();
const APIResponse_list_PosRecentOrderResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosRecentOrderResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosOrderItemRequest = z
  .object({
    item_id: z.union([z.number(), z.null()]).optional(),
    item_code: z.union([z.string(), z.null()]).optional(),
    item_name: z.union([z.string(), z.null()]).optional(),
    quantity: z.union([z.number(), z.string()]),
    unit_price: z.union([z.number(), z.string()]),
    original_price: z.union([z.number(), z.string(), z.null()]).optional(),
    discount: z.union([z.number(), z.string(), z.null()]).optional().default(0),
    amount: z.union([z.number(), z.string()]),
  })
  .passthrough();
const PosPlaceOrderRequest = z
  .object({
    items: z.array(PosOrderItemRequest).min(1),
    payment_received: z
      .union([z.number(), z.string(), z.null()])
      .optional()
      .default(0),
    member: z.union([z.string(), z.null()]).optional(),
    rep: z.union([z.string(), z.null()]).optional(),
    pricemode: z.union([z.string(), z.null()]).optional(),
    refno: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosInvoicePaymentRequest = z
  .object({
    paymode: z.string().min(1).max(10),
    amount: z.union([z.number(), z.string()]),
    card_type_code: z.union([z.string(), z.null()]).optional(),
    card_station_no: z.union([z.string(), z.null()]).optional(),
    card_approval_code: z.union([z.string(), z.null()]).optional(),
    card_last_digits: z.union([z.string(), z.null()]).optional(),
    card_last4: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosInvoicePromotionRequest = z
  .object({
    promo_id: z.number().int().gt(0),
    promo_code: z.union([z.string(), z.null()]).optional(),
    promo_name: z.union([z.string(), z.null()]).optional(),
    promo_disper: z.union([z.number(), z.string(), z.null()]).optional(),
    promo_disamt: z.union([z.number(), z.string(), z.null()]).optional(),
    promo_free_item: z.union([z.string(), z.null()]).optional(),
    promo_free_voucher: z.union([z.string(), z.null()]).optional(),
    promo_discount_amount: z
      .union([z.number(), z.string(), z.null()])
      .optional(),
    offer_qty: z.union([z.number(), z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosCreateInvoiceRequest = z
  .object({
    customer_id: z.union([z.number(), z.null()]).optional(),
    customer_code: z.union([z.string(), z.null()]).optional(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    sales_rep_id: z.union([z.string(), z.null()]).optional(),
    cashier_id: z.union([z.string(), z.null()]).optional(),
    payment_received: z
      .union([z.number(), z.string(), z.null()])
      .optional()
      .default(0),
    bill_discount_percentage: z
      .union([z.number(), z.string(), z.null()])
      .optional()
      .default(0),
    bill_discount_amount: z
      .union([z.number(), z.string(), z.null()])
      .optional()
      .default(0),
    invoice_date: z.union([z.string(), z.null()]).optional(),
    paymode: z.union([z.string(), z.null()]).optional().default('CSH'),
    payments: z.array(PosInvoicePaymentRequest).optional(),
    promotions: z.array(PosInvoicePromotionRequest).optional(),
    items: z.array(PosOrderItemRequest).min(1),
    original_invoice_no: z.union([z.string(), z.null()]).optional(),
    tax_per: z.union([z.number(), z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosRecentInvoiceItemResponse = z
  .object({
    item_id: z.union([z.number(), z.null()]).optional(),
    item_code: z.union([z.string(), z.null()]).optional(),
    item_name: z.union([z.string(), z.null()]).optional(),
    quantity: z.union([z.number(), z.null()]),
    unit_price: z.union([z.number(), z.null()]),
    original_price: z.union([z.number(), z.null()]).optional(),
    discount: z.union([z.number(), z.null()]).optional(),
    amount: z.union([z.number(), z.null()]),
  })
  .passthrough();
const PosRecentInvoiceResponse = z
  .object({
    invoice_no: z.string(),
    item_count: z.number().int(),
    amount: z.union([z.number(), z.null()]),
    customer_name: z.union([z.string(), z.null()]).optional(),
    created_at: z.union([z.string(), z.null()]).optional(),
    items: z.array(PosRecentInvoiceItemResponse).optional(),
    tax_per: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_list_PosRecentInvoiceResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosRecentInvoiceResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosInvoicePaymentResponse = z
  .object({
    paymode: z.string(),
    paytypedesc: z.union([z.string(), z.null()]).optional(),
    amount: z.union([z.number(), z.null()]),
    is_credit: z.boolean().optional().default(false),
    crdcardno: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosInvoiceDetailResponse = z
  .object({
    invoice_no: z.string(),
    customer_id: z.union([z.number(), z.null()]).optional(),
    customer_code: z.union([z.string(), z.null()]).optional(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    customer_address: z.union([z.string(), z.null()]).optional(),
    customer_tin_no: z.union([z.string(), z.null()]).optional(),
    cashier_id: z.union([z.string(), z.null()]).optional(),
    created_at: z.union([z.string(), z.null()]).optional(),
    gross_amount: z.union([z.number(), z.null()]).optional().default(0),
    bill_discount_percentage: z
      .union([z.number(), z.null()])
      .optional()
      .default(0),
    bill_discount_amount: z.union([z.number(), z.null()]).optional().default(0),
    net_amount: z.union([z.number(), z.null()]).optional().default(0),
    total_paid: z.union([z.number(), z.null()]).optional().default(0),
    change_amount: z.union([z.number(), z.null()]).optional().default(0),
    refno: z.union([z.string(), z.null()]).optional(),
    points_added: z.union([z.number(), z.null()]).optional(),
    customer_total_points: z.union([z.number(), z.null()]).optional(),
    promotion_discount_amount: z.union([z.number(), z.null()]).optional(),
    promo_free_item: z.union([z.string(), z.null()]).optional(),
    promo_free_voucher: z.union([z.string(), z.null()]).optional(),
    promotions: z.array(z.object({}).partial().passthrough()).optional(),
    items: z.array(PosRecentInvoiceItemResponse).optional(),
    payments: z.array(PosInvoicePaymentResponse).optional(),
    tax_per: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_PosInvoiceDetailResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PosInvoiceDetailResponse, z.null()]),
  })
  .partial()
  .passthrough();
const SelectiveReturnItemRequest = z
  .object({
    item_code: z.string().min(1),
    return_quantity: z.number().gt(0),
    selected: z.boolean().optional().default(true),
  })
  .passthrough();
const SelectiveReturnRequest = z
  .object({
    original_invoice_no: z.string().min(1),
    customer_id: z.union([z.number(), z.null()]).optional(),
    customer_code: z.union([z.string(), z.null()]).optional(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    cashier_id: z.union([z.string(), z.null()]).optional(),
    items: z.array(SelectiveReturnItemRequest).min(1),
    new_items: z.array(PosOrderItemRequest).optional(),
    return_reason: z.union([z.string(), z.null()]).optional(),
    paymode: z.string().max(10).optional().default('CSH'),
    payment_received: z.union([z.number(), z.string()]).optional().default(0),
    bill_discount_percentage: z
      .union([z.number(), z.string()])
      .optional()
      .default(0),
    bill_discount_amount: z
      .union([z.number(), z.string()])
      .optional()
      .default(0),
    payments: z.array(PosInvoicePaymentRequest).optional(),
    promotions: z.array(PosInvoicePromotionRequest).optional(),
  })
  .passthrough();
const SelectiveReturnResponse = z
  .object({
    return_invoice_no: z.string(),
    original_invoice_no: z.string(),
    item_count: z.number().int(),
    return_amount: z.union([z.number(), z.null()]),
    customer_name: z.union([z.string(), z.null()]).optional(),
    created_at: z.union([z.string(), z.null()]).optional(),
    status: z.string().optional().default('success'),
    message: z.string().optional().default('Return processed successfully'),
  })
  .passthrough();
const APIResponse_SelectiveReturnResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([SelectiveReturnResponse, z.null()]),
  })
  .partial()
  .passthrough();
const DirectReturnItemRequest = z
  .object({
    item_code: z.string().min(1),
    item_name: z.string().min(1),
    return_quantity: z.number().gt(0),
    unit_price: z.union([z.number(), z.string()]),
    discount: z.union([z.number(), z.string()]).optional().default(0),
  })
  .passthrough();
const DirectReturnRequest = z
  .object({
    customer_id: z.union([z.number(), z.null()]).optional(),
    customer_code: z.union([z.string(), z.null()]).optional(),
    customer_name: z.union([z.string(), z.null()]).optional(),
    cashier_id: z.union([z.string(), z.null()]).optional(),
    items: z.array(DirectReturnItemRequest).min(1),
    return_reason: z.union([z.string(), z.null()]).optional(),
    paymode: z.string().max(10).optional().default('CSH'),
  })
  .passthrough();
const DirectReturnResponse = z
  .object({
    return_invoice_no: z.string(),
    item_count: z.number().int(),
    return_amount: z.union([z.number(), z.null()]),
    customer_name: z.union([z.string(), z.null()]).optional(),
    created_at: z.union([z.string(), z.null()]).optional(),
    status: z.string().optional().default('success'),
    message: z
      .string()
      .optional()
      .default('Direct return processed successfully'),
  })
  .passthrough();
const APIResponse_DirectReturnResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([DirectReturnResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PosSetupResponse = z
  .object({
    id: z.string(),
    setup_orders: z.union([z.boolean(), z.null()]).optional(),
    setup_comname: z.union([z.string(), z.null()]).optional(),
    setup_comaddress: z.union([z.string(), z.null()]).optional(),
    setup_comtele: z.union([z.string(), z.null()]).optional(),
    setup_comlogo: z.union([z.string(), z.null()]).optional(),
    setup_storeid: z.union([z.string(), z.null()]).optional(),
    setup_allowminus: z.union([z.boolean(), z.null()]).optional(),
    setup_invnotes: z.union([z.string(), z.null()]).optional(),
    setup_quotenotes: z.union([z.string(), z.null()]).optional(),
    setup_tax: z.union([z.string(), z.null()]).optional(),
    setup_vatacc: z.union([z.string(), z.null()]).optional(),
    setup_vatexpacc: z.union([z.string(), z.null()]).optional(),
    setup_pointscheme: z.union([z.number(), z.null()]).optional(),
    setup_tinno: z.union([z.string(), z.null()]).optional(),
    setup_regno: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_PosSetupResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PosSetupResponse, z.null()]),
  })
  .partial()
  .passthrough();
const Body_update_company_logo_api_pos_setup_logo_put = z
  .object({ logo: z.instanceof(File) })
  .passthrough();
const PosTraTypeResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    tratype: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    status: z.union([z.boolean(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_PosTraTypeResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosTraTypeResponse), z.null()]),
  })
  .partial()
  .passthrough();
const OtherPaymentsHistoryItemResponse = z
  .object({
    doc_no: z.string(),
    created_at: z.union([z.string(), z.null()]).optional(),
    total_amount: z.union([z.number(), z.null()]),
    line_count: z.number().int(),
  })
  .passthrough();
const APIResponse_list_OtherPaymentsHistoryItemResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(OtherPaymentsHistoryItemResponse), z.null()]),
  })
  .partial()
  .passthrough();
const OtherPaymentsDetailItemResponse = z
  .object({
    id: z.number().int(),
    doc_no: z.string(),
    created_at: z.union([z.string(), z.null()]).optional(),
    account_no: z.union([z.string(), z.null()]).optional(),
    account_name: z.union([z.string(), z.null()]).optional(),
    pay_to: z.union([z.string(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    amount: z.union([z.number(), z.null()]),
  })
  .passthrough();
const APIResponse_list_OtherPaymentsDetailItemResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(OtherPaymentsDetailItemResponse), z.null()]),
  })
  .partial()
  .passthrough();
const OtherPaymentLineRequest = z
  .object({
    account_name: z.string(),
    account_no: z.union([z.string(), z.null()]).optional(),
    pay_to: z.union([z.string(), z.null()]).optional(),
    description: z.union([z.string(), z.null()]).optional(),
    amount: z.union([z.number(), z.string()]),
  })
  .passthrough();
const OtherPaymentsSaveRequest = z
  .object({ rows: z.array(OtherPaymentLineRequest) })
  .passthrough();
const OtherPaymentsSaveResponse = z
  .object({
    doc_no: z.string(),
    inserted_count: z.number().int(),
    transaction_ids: z.array(z.number().int()),
    cash_paymode: z.string(),
    cash_account: z.string(),
    cash_transaction_type_id: z.number().int(),
  })
  .passthrough();
const APIResponse_OtherPaymentsSaveResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([OtherPaymentsSaveResponse, z.null()]),
  })
  .partial()
  .passthrough();
const SystemMenuResponse = z
  .object({
    id: z.union([z.number(), z.null()]),
    label: z.union([z.string(), z.null()]),
    to_url: z.union([z.string(), z.null()]),
    parent_id: z.union([z.number(), z.null()]),
    order_num: z.union([z.number(), z.null()]),
    component: z.union([z.string(), z.null()]),
    icon: z.union([z.string(), z.null()]),
    has_menu: z.union([z.boolean(), z.null()]).default(true),
  })
  .partial()
  .passthrough();
const APIResponse_List_SystemMenuResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SystemMenuResponse), z.null()]),
  })
  .partial()
  .passthrough();
const SystemMenuWithChildrenResponse: z.ZodType<SystemMenuWithChildrenResponse> =
  z.lazy(() =>
    z
      .object({
        id: z.union([z.number(), z.null()]),
        label: z.union([z.string(), z.null()]),
        to_url: z.union([z.string(), z.null()]),
        parent_id: z.union([z.number(), z.null()]),
        order_num: z.union([z.number(), z.null()]),
        component: z.union([z.string(), z.null()]),
        icon: z.union([z.string(), z.null()]),
        has_menu: z.union([z.boolean(), z.null()]).default(true),
        c_at: z.union([z.string(), z.null()]),
        c_by: z.union([z.number(), z.null()]),
        m_at: z.union([z.string(), z.null()]),
        m_by: z.union([z.number(), z.null()]),
        children: z.array(SystemMenuWithChildrenResponse).default([]),
      })
      .partial()
      .passthrough()
  );
const APIResponse_List_SystemMenuWithChildrenResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(SystemMenuWithChildrenResponse), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_SystemMenuResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([SystemMenuResponse, z.null()]),
  })
  .partial()
  .passthrough();
const app__api__routes__pos_cart__CartItemRequest = z
  .object({
    item_id: z.number().int(),
    item_code: z.string(),
    item_name: z.string(),
    qty: z.number(),
    unit_price: z.number(),
    discount: z.number().optional().default(0),
    total: z.number(),
  })
  .passthrough();
const CartItemUpdateRequest = z
  .object({ qty: z.number(), discount: z.number(), total: z.number() })
  .passthrough();
const DocumentRequest = z
  .object({
    header: z.object({}).partial().passthrough(),
    details: z.array(z.object({}).partial().passthrough()).optional(),
    save_mode: z.enum(['temporary', 'final']).optional().default('final'),
  })
  .passthrough();
const StageLineRequest = z
  .object({
    detail: z.object({}).partial().passthrough(),
    line_no: z.number().int(),
    temp_no: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const AccountAllocationItem = z
  .object({
    transactionId: z.union([z.number(), z.null()]).optional(),
    traNo: z.union([z.string(), z.null()]),
    traType: z.union([z.string(), z.null()]),
    accountNo: z.union([z.string(), z.null()]),
    traAmount: z.union([z.number(), z.string(), z.null()]).optional(),
    allcamount: z.union([z.number(), z.string()]),
  })
  .passthrough();
const AccountPaymentRequest = z
  .object({
    membercode: z.string(),
    location: z.union([z.string(), z.null()]).optional(),
    paymode: z.string(),
    allocated: z.array(AccountAllocationItem),
    party_type: z.union([z.string(), z.null()]).optional(),
    payment_desc: z.union([z.string(), z.null()]).optional(),
    cheque_no: z.union([z.string(), z.null()]).optional(),
    card_reference: z.union([z.string(), z.null()]).optional(),
    other_reference: z.union([z.string(), z.null()]).optional(),
    payer_name: z.union([z.string(), z.null()]).optional(),
    reference_no: z.union([z.string(), z.null()]).optional(),
    note: z.union([z.string(), z.null()]).optional(),
    cheque_date: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const LineDiscountRequest = z
  .object({
    quantity: z.number(),
    unit_price: z.number(),
    discount_percentage: z.number().optional().default(0),
    discount_amount: z.number().optional().default(0),
  })
  .passthrough();
const Body_upload_jrxml_with_uuid_api_report_upload_jrxml__report_uuid__post = z
  .object({ file: z.instanceof(File) })
  .passthrough();
const PosItemResourceCreate = z
  .object({
    resource_itemlots_code: z.string(),
    resource_type: z.string(),
    resource_path: z.string(),
    resource_sort_order: z.union([z.number(), z.null()]).optional(),
    resource_title: z.union([z.string(), z.null()]).optional(),
    is_approved: z.union([z.number(), z.null()]).optional().default(0),
    is_primary: z.union([z.number(), z.null()]).optional().default(0),
  })
  .passthrough();
const PosItemResourceResponse = z
  .object({
    resource_itemlots_code: z.union([z.string(), z.null()]),
    resource_type: z.union([z.string(), z.null()]),
    resource_path: z.union([z.string(), z.null()]),
    resource_sort_order: z.union([z.number(), z.null()]),
    resource_title: z.union([z.string(), z.null()]),
    is_approved: z.union([z.number(), z.null()]).default(0),
    is_primary: z.union([z.number(), z.null()]).default(0),
    resource_id: z.union([z.number(), z.null()]),
    resource_crdate: z.union([z.string(), z.null()]),
    resource_crby: z.union([z.string(), z.null()]),
    resource_mddate: z.union([z.string(), z.null()]),
    resource_mdby: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_PosItemResourceResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PosItemResourceResponse, z.null()]),
  })
  .partial()
  .passthrough();
const APIResponse_List_PosItemResourceResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosItemResourceResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosItemResourceUpdate = z
  .object({
    resource_itemlots_code: z.string(),
    resource_type: z.string(),
    resource_path: z.string(),
    resource_sort_order: z.union([z.number(), z.null()]).optional(),
    resource_title: z.union([z.string(), z.null()]).optional(),
    is_approved: z.union([z.number(), z.null()]).optional().default(0),
    is_primary: z.union([z.number(), z.null()]).optional().default(0),
  })
  .passthrough();
const Body_upload_resource_file_api_pos_item_resource_upload_post = z
  .object({
    file: z.instanceof(File),
    resource_type: z.string().optional().default('image'),
    itemlots_code: z.string().optional().default(''),
  })
  .passthrough();
const PosPromoDetResponse = z
  .object({
    line_seq: z.number().int().optional().default(0),
    criteria_type: z.string(),
    criteria_value: z.union([z.string(), z.null()]).optional(),
    amount_min: z.union([z.number(), z.null()]).optional(),
    amount_max: z.union([z.number(), z.null()]).optional(),
    id: z.union([z.number(), z.null()]).optional(),
    promo_id: z.union([z.number(), z.null()]).optional(),
  })
  .passthrough();
const PosPromoHedResponse = z
  .object({
    promo_code: z.string(),
    promo_name: z.string(),
    promo_desc: z.union([z.string(), z.null()]).optional(),
    promo_type_id: z.number().int(),
    promo_datefrom: z.string(),
    promo_dateto: z.string(),
    promo_timefrom: z.string(),
    promo_timeto: z.string(),
    promo_days: z.union([z.number(), z.null()]).optional(),
    promo_active: z.boolean().optional().default(true),
    promo_disper: z.union([z.number(), z.null()]).optional(),
    promo_disamt: z.union([z.number(), z.null()]).optional(),
    promo_free_item: z.union([z.string(), z.null()]).optional(),
    promo_free_voucher: z.union([z.string(), z.null()]).optional(),
    id: z.union([z.number(), z.null()]).optional(),
    details: z.array(PosPromoDetResponse).optional().default([]),
    promo_type_code: z.union([z.string(), z.null()]).optional(),
    promo_status: z.string().optional().default('pending'),
    promo_approved_by: z.union([z.string(), z.null()]).optional(),
    promo_approved_at: z.union([z.string(), z.null()]).optional(),
    promo_reject_reason: z.union([z.string(), z.null()]).optional(),
    promo_crby: z.union([z.string(), z.null()]).optional(),
    promo_crat: z.union([z.string(), z.null()]).optional(),
    promo_mdby: z.union([z.string(), z.null()]).optional(),
    promo_mdat: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
const APIResponse_List_PosPromoHedResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosPromoHedResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosPromoDetCreate = z
  .object({
    line_seq: z.number().int().optional().default(0),
    criteria_type: z.string(),
    criteria_value: z.union([z.string(), z.null()]).optional(),
    amount_min: z.union([z.number(), z.string(), z.null()]).optional(),
    amount_max: z.union([z.number(), z.string(), z.null()]).optional(),
  })
  .passthrough();
const PosPromoHedCreate = z
  .object({
    promo_code: z.string(),
    promo_name: z.string(),
    promo_desc: z.union([z.string(), z.null()]).optional(),
    promo_type_id: z.number().int(),
    promo_datefrom: z.string(),
    promo_dateto: z.string(),
    promo_timefrom: z.string(),
    promo_timeto: z.string(),
    promo_days: z.union([z.number(), z.null()]).optional(),
    promo_active: z.boolean().optional().default(true),
    promo_disper: z.union([z.number(), z.string(), z.null()]).optional(),
    promo_disamt: z.union([z.number(), z.string(), z.null()]).optional(),
    promo_free_item: z.union([z.string(), z.null()]).optional(),
    promo_free_voucher: z.union([z.string(), z.null()]).optional(),
    details: z.array(PosPromoDetCreate).optional().default([]),
  })
  .passthrough();
const APIResponse_PosPromoHedResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PosPromoHedResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PosPromoRejectRequest = z
  .object({ reason: z.union([z.string(), z.null()]) })
  .partial()
  .passthrough();
const PosPromoHedUpdate = z
  .object({
    promo_code: z.string(),
    promo_name: z.string(),
    promo_desc: z.union([z.string(), z.null()]).optional(),
    promo_type_id: z.number().int(),
    promo_datefrom: z.string(),
    promo_dateto: z.string(),
    promo_timefrom: z.string(),
    promo_timeto: z.string(),
    promo_days: z.union([z.number(), z.null()]).optional(),
    promo_active: z.boolean().optional().default(true),
    promo_disper: z.union([z.number(), z.string(), z.null()]).optional(),
    promo_disamt: z.union([z.number(), z.string(), z.null()]).optional(),
    promo_free_item: z.union([z.string(), z.null()]).optional(),
    promo_free_voucher: z.union([z.string(), z.null()]).optional(),
    details: z.array(PosPromoDetCreate).optional().default([]),
  })
  .passthrough();
const app__schemas__pos_promotion_selection__CartItemRequest = z
  .object({
    itemCode: z.union([z.string(), z.null()]),
    itemGroup: z.union([z.string(), z.null()]),
    qty: z.union([z.number(), z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PromotionSelectionRequest = z
  .object({
    trigger: z.union([
      z.enum(['member_changed', 'paymode_selected', 'card_type_selected']),
      z.null(),
    ]),
    activeTypeCodes: z.array(z.string()),
    paymode: z.union([z.string(), z.null()]),
    cardTypeCode: z.union([z.string(), z.null()]),
    paymodes: z.array(z.string()),
    cartItems: z.array(app__schemas__pos_promotion_selection__CartItemRequest),
    subtotal: z.union([z.number(), z.string()]).default(0),
    customerGroupId: z.union([z.number(), z.null()]),
    locationId: z.union([z.string(), z.null()]),
  })
  .partial()
  .passthrough();
const PromotionSelectionResponse = z
  .object({
    promo_id: z.union([z.number(), z.null()]),
    promo_code: z.union([z.string(), z.null()]),
    promo_name: z.union([z.string(), z.null()]),
    promo_type_code: z.union([z.string(), z.null()]),
    promo_disper: z.union([z.number(), z.null()]),
    promo_disamt: z.union([z.number(), z.null()]),
    promo_free_item: z.union([z.string(), z.null()]),
    promo_free_voucher: z.union([z.string(), z.null()]),
    details: z.array(z.object({}).partial().passthrough()),
  })
  .partial()
  .passthrough();
const PromotionSelectionListResponse = z
  .object({ promotions: z.array(PromotionSelectionResponse) })
  .partial()
  .passthrough();
const APIResponse_PromotionSelectionListResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([PromotionSelectionListResponse, z.null()]),
  })
  .partial()
  .passthrough();
const PosPromoTypeListResponse = z
  .object({
    id: z.number().int(),
    type_code: z.string(),
    type_name: z.string(),
    type_desc: z.union([z.string(), z.null()]).optional(),
    criteria_pattern: z.union([z.unknown(), z.null()]).optional(),
    type_priority: z.number().int(),
  })
  .passthrough();
const APIResponse_List_PosPromoTypeListResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosPromoTypeListResponse), z.null()]),
  })
  .partial()
  .passthrough();
const PosCustomerGroupResponse = z
  .object({
    id: z.number().int(),
    group_name: z.string(),
    group_description: z.union([z.string(), z.null()]).optional(),
    active: z.boolean(),
  })
  .passthrough();
const APIResponse_List_PosCustomerGroupResponse__ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([z.array(PosCustomerGroupResponse), z.null()]),
  })
  .partial()
  .passthrough();
const RootResponse = z
  .object({
    version: z.string(),
    environment: z.string(),
    app_name: z.string(),
  })
  .passthrough();
const APIResponse_RootResponse_ = z
  .object({
    status: z.union([z.number(), z.null()]).default(200),
    message: z.union([z.string(), z.null()]).default('Success'),
    data: z.union([RootResponse, z.null()]),
  })
  .partial()
  .passthrough();

export const schemas = {
  LoginRequest,
  PosLoc,
  ItUserGroup,
  ItInstitute,
  LoginResponse,
  APIResponse_LoginResponse_,
  ValidationError,
  HTTPValidationError,
  APIResponse,
  RefreshTokenResponse,
  APIResponse_RefreshTokenResponse_,
  FirstTimeLoginRequest,
  AuthResponse,
  APIResponse_AuthResponse_,
  UserWithActiveStatusResponse,
  APIResponse_list_UserWithActiveStatusResponse__,
  UserPermissionResponse,
  APIResponse_list_UserPermissionResponse__,
  UserMenuResponse,
  APIResponse_list_UserMenuResponse__,
  UserGroupsResponse,
  APIResponse_list_UserGroupsResponse__,
  UpdateFavoriteMenuRequest,
  UpdateFavouriteMenuResponse,
  APIResponse_UpdateFavouriteMenuResponse_,
  UserRole,
  UserCreateRequest,
  UserCreateResponse,
  APIResponse_UserCreateResponse_,
  APIResponse_list_int__,
  APIResponse_str_,
  APIResponse_bool_,
  SystemPermissionResponse,
  APIResponse_list_SystemPermissionResponse__,
  PosItemUpdateRequest,
  APIResponse_dict_,
  search,
  PosItemResponse,
  APIResponse_List_PosItemResponse__,
  PosCustomerUpdateRequest,
  PosCustomerResponse,
  APIResponse_List_PosCustomerResponse__,
  PosVendorUpdateRequest,
  PosVendorResponse,
  APIResponse_List_PosVendorResponse__,
  floatamt,
  SignonUpdateRequest,
  update_user_signon_records_api_api_signon_user_records__put_Body,
  SignoffRequest,
  signoff_user_api_api_signon_signoff__put_Body,
  PosCashRecWithCreate,
  PosCashRecWithResponse,
  APIResponse_List_PosCashRecWithResponse__,
  APIResponse_PosCashRecWithResponse_,
  PosCashRecWithUpdate,
  PosItemGroupCreate,
  PosItemGroupResponse,
  APIResponse_PosItemGroupResponse_,
  APIResponse_List_PosItemGroupResponse__,
  PosItemGroupUpdate,
  PosGLAccountCreate,
  PosGLAccountResponse,
  APIResponse_PosGLAccountResponse_,
  APIResponse_list_PosGLAccountResponse__,
  PosGLAccountSuggestionResponse,
  APIResponse_list_PosGLAccountSuggestionResponse__,
  PosGLAccountUpdate,
  APIResponse_List_Dict_str__Any___,
  APIResponse_Dict_str__Any__,
  PosItemLotsResponse,
  APIResponse_List_PosItemLotsResponse__,
  PosPayModeResponse,
  APIResponse_List_PosPayModeResponse__,
  PosPayModeDetResponse,
  APIResponse_List_PosPayModeDetResponse__,
  PosRecentOrderItemResponse,
  PosRecentOrderResponse,
  APIResponse_list_PosRecentOrderResponse__,
  PosOrderItemRequest,
  PosPlaceOrderRequest,
  PosInvoicePaymentRequest,
  PosInvoicePromotionRequest,
  PosCreateInvoiceRequest,
  PosRecentInvoiceItemResponse,
  PosRecentInvoiceResponse,
  APIResponse_list_PosRecentInvoiceResponse__,
  PosInvoicePaymentResponse,
  PosInvoiceDetailResponse,
  APIResponse_PosInvoiceDetailResponse_,
  SelectiveReturnItemRequest,
  SelectiveReturnRequest,
  SelectiveReturnResponse,
  APIResponse_SelectiveReturnResponse_,
  DirectReturnItemRequest,
  DirectReturnRequest,
  DirectReturnResponse,
  APIResponse_DirectReturnResponse_,
  PosSetupResponse,
  APIResponse_PosSetupResponse_,
  Body_update_company_logo_api_pos_setup_logo_put,
  PosTraTypeResponse,
  APIResponse_List_PosTraTypeResponse__,
  OtherPaymentsHistoryItemResponse,
  APIResponse_list_OtherPaymentsHistoryItemResponse__,
  OtherPaymentsDetailItemResponse,
  APIResponse_list_OtherPaymentsDetailItemResponse__,
  OtherPaymentLineRequest,
  OtherPaymentsSaveRequest,
  OtherPaymentsSaveResponse,
  APIResponse_OtherPaymentsSaveResponse_,
  SystemMenuResponse,
  APIResponse_List_SystemMenuResponse__,
  SystemMenuWithChildrenResponse,
  APIResponse_List_SystemMenuWithChildrenResponse__,
  APIResponse_SystemMenuResponse_,
  app__api__routes__pos_cart__CartItemRequest,
  CartItemUpdateRequest,
  DocumentRequest,
  StageLineRequest,
  AccountAllocationItem,
  AccountPaymentRequest,
  LineDiscountRequest,
  Body_upload_jrxml_with_uuid_api_report_upload_jrxml__report_uuid__post,
  PosItemResourceCreate,
  PosItemResourceResponse,
  APIResponse_PosItemResourceResponse_,
  APIResponse_List_PosItemResourceResponse__,
  PosItemResourceUpdate,
  Body_upload_resource_file_api_pos_item_resource_upload_post,
  PosPromoDetResponse,
  PosPromoHedResponse,
  APIResponse_List_PosPromoHedResponse__,
  PosPromoDetCreate,
  PosPromoHedCreate,
  APIResponse_PosPromoHedResponse_,
  PosPromoRejectRequest,
  PosPromoHedUpdate,
  app__schemas__pos_promotion_selection__CartItemRequest,
  PromotionSelectionRequest,
  PromotionSelectionResponse,
  PromotionSelectionListResponse,
  APIResponse_PromotionSelectionListResponse_,
  PosPromoTypeListResponse,
  APIResponse_List_PosPromoTypeListResponse__,
  PosCustomerGroupResponse,
  APIResponse_List_PosCustomerGroupResponse__,
  RootResponse,
  APIResponse_RootResponse_,
};

const endpoints = makeApi([
  {
    method: 'get',
    path: '/',
    alias: 'root__get',
    requestFormat: 'json',
    response: APIResponse_RootResponse_,
  },
  {
    method: 'get',
    path: '/api/api/signon/check_status',
    alias: 'check_user_signon_status_api_api_signon_check_status_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/api/signon/check_status/',
    alias: 'check_user_signon_status_api_api_signon_check_status__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/api/signon/server_datetime',
    alias: 'get_server_datetime_route_api_api_signon_server_datetime_get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'get',
    path: '/api/api/signon/server_datetime/',
    alias: 'get_server_datetime_route_api_api_signon_server_datetime__get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'put',
    path: '/api/api/signon/signoff',
    alias: 'signoff_user_api_api_signon_signoff_put',
    description: `Sign off a user in it_user_master:
- signon &#x3D; 0
- signoff &#x3D; 1
- signoffdate/signofftime &#x3D; NOW()`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: signoff_user_api_api_signon_signoff__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/api/signon/signoff/',
    alias: 'signoff_user_api_api_signon_signoff__put',
    description: `Sign off a user in it_user_master:
- signon &#x3D; 0
- signoff &#x3D; 1
- signoffdate/signofftime &#x3D; NOW()`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: signoff_user_api_api_signon_signoff__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/api/signon/user_records',
    alias: 'get_user_signon_records_api_api_signon_user_records_get',
    description: `Retrieve sign-on records from it_user_master table for a given user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/api/signon/user_records',
    alias: 'update_user_signon_records_api_api_signon_user_records_put',
    description: `Update sign-on records in it_user_master by username.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          update_user_signon_records_api_api_signon_user_records__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signon',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'signondate',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signontime',
        type: 'Query',
        schema: search,
      },
      {
        name: 'station_id',
        type: 'Query',
        schema: search,
      },
      {
        name: 'stationid',
        type: 'Query',
        schema: search,
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/api/signon/user_records/',
    alias: 'get_user_signon_records_api_api_signon_user_records__get',
    description: `Retrieve sign-on records from it_user_master table for a given user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/api/signon/user_records/',
    alias: 'update_user_signon_records_api_api_signon_user_records__put',
    description: `Update sign-on records in it_user_master by username.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          update_user_signon_records_api_api_signon_user_records__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signon',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'signondate',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signontime',
        type: 'Query',
        schema: search,
      },
      {
        name: 'station_id',
        type: 'Query',
        schema: search,
      },
      {
        name: 'stationid',
        type: 'Query',
        schema: search,
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/auth/first_time_login',
    alias: 'first_time_login_api_auth_first_time_login_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: FirstTimeLoginRequest,
      },
    ],
    response: APIResponse_LoginResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/auth/login',
    alias: 'login_api_auth_login_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LoginRequest,
      },
    ],
    response: APIResponse_LoginResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/auth/logout',
    alias: 'logout_api_auth_logout_post',
    requestFormat: 'json',
    response: APIResponse,
  },
  {
    method: 'post',
    path: '/api/auth/refresh',
    alias: 'refresh_token_api_auth_refresh_post',
    requestFormat: 'json',
    response: APIResponse_RefreshTokenResponse_,
  },
  {
    method: 'get',
    path: '/api/it_product/all',
    alias: 'load_items_for_management_api_it_product_all_get',
    description: `Load active items for ItemManagement page.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(100),
      },
      {
        name: 'search',
        type: 'Query',
        schema: search,
      },
      {
        name: 'status',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_List_PosItemResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/item-lots/:item_code',
    alias: 'get_item_lot_api_item_lots__item_code__get',
    description: `Get item lot details with product info.

Filtered by &#x60;&#x60;location&#x60;&#x60; when supplied (e.g. the source branch of a stock
transfer), otherwise by the logged-in user&#x27;s active branch.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'item_code',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'lot_code',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'location',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/item-lots/:item_code/case-qty',
    alias: 'get_item_case_qty_api_item_lots__item_code__case_qty_get',
    description: `Get case quantity for an item from pos_item table.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'item_code',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/item-lots/search/:search_text',
    alias: 'search_item_lots_api_item_lots_search__search_text__get',
    description: `Search item lots by code, description, or barcode.

Filtered by &#x60;&#x60;location&#x60;&#x60; when supplied (e.g. the source branch of a stock
transfer), otherwise by the logged-in user&#x27;s active branch.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'search_text',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'location',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_List_Dict_str__Any___,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/language/all',
    alias: 'get_available_languages_api_language_all_get',
    description: `Return list of available languages for reports.`,
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/menus/:menu_id',
    alias: 'get_menu_by_id_api_menus__menu_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'menu_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_SystemMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/all',
    alias: 'get_all_system_menus_api_menus_all_get',
    requestFormat: 'json',
    response: APIResponse_List_SystemMenuResponse__,
  },
  {
    method: 'get',
    path: '/api/menus/by-component/:component',
    alias: 'get_menu_by_component_api_menus_by_component__component__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'component',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_SystemMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/by-url/:url',
    alias: 'get_menu_by_url_api_menus_by_url__url__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'url',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_SystemMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/children/:parent_id',
    alias: 'get_menu_children_api_menus_children__parent_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'parent_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_List_SystemMenuResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/menus/hierarchy',
    alias: 'get_menu_hierarchy_api_menus_hierarchy_get',
    requestFormat: 'json',
    response: APIResponse_List_SystemMenuWithChildrenResponse__,
  },
  {
    method: 'get',
    path: '/api/menus/parent',
    alias: 'get_parent_system_menus_api_menus_parent_get',
    requestFormat: 'json',
    response: APIResponse_List_SystemMenuResponse__,
  },
  {
    method: 'get',
    path: '/api/other_payments/:doc_no/details',
    alias: 'get_payment_details_api_other_payments__doc_no__details_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'doc_no',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_list_OtherPaymentsDetailItemResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/other_payments/account-suggestions',
    alias: 'get_account_suggestions_api_other_payments_account_suggestions_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'q',
        type: 'Query',
        schema: z.string().optional().default(''),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(20).optional().default(10),
      },
    ],
    response: APIResponse_list_PosGLAccountSuggestionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/other_payments/recent',
    alias: 'get_recent_payments_api_other_payments_recent_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(20).optional().default(10),
      },
    ],
    response: APIResponse_list_OtherPaymentsHistoryItemResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/other_payments/save',
    alias: 'save_other_payments_api_other_payments_save_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: OtherPaymentsSaveRequest,
      },
    ],
    response: APIResponse_OtherPaymentsSaveResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_cash_rec_with',
    alias: 'get_all_payment_records_api_pos_cash_rec_with_get',
    description: `Get all payment records with pagination and optional filters`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'skip',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional().default(100),
      },
      {
        name: 'payment_loc',
        type: 'Query',
        schema: search,
      },
      {
        name: 'payment_date',
        type: 'Query',
        schema: search,
      },
      {
        name: 'payment_station',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'payment_shift',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_List_PosCashRecWithResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_cash_rec_with/:payment_no',
    alias: 'get_payment_record_api_pos_cash_rec_with__payment_no__get',
    description: `Get a specific payment record by Payment_No`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'payment_no',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosCashRecWithResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_cash_rec_with/all',
    alias: 'get_all_payment_records_api_pos_cash_rec_with_all_get',
    description: `Get all payment records with pagination and optional filters`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'skip',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional().default(100),
      },
      {
        name: 'payment_loc',
        type: 'Query',
        schema: search,
      },
      {
        name: 'payment_date',
        type: 'Query',
        schema: search,
      },
      {
        name: 'payment_station',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'payment_shift',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_List_PosCashRecWithResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_cash_rec_with/cashier/:cashier_id',
    alias:
      'get_payments_by_cashier_api_pos_cash_rec_with_cashier__cashier_id__get',
    description: `Get payment records for a specific cashier`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'cashier_id',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'skip',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional().default(100),
      },
    ],
    response: APIResponse_List_PosCashRecWithResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_cash_rec_with/cashier/:cashier_id/total',
    alias:
      'get_cashier_total_api_pos_cash_rec_with_cashier__cashier_id__total_get',
    description: `Get total payment amount for a cashier`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'cashier_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_cash_rec_with/create',
    alias: 'create_payment_record_api_pos_cash_rec_with_create_post',
    description: `Create a new payment cash receive with record`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosCashRecWithCreate,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/pos_cash_rec_with/delete/:payment_no',
    alias:
      'delete_payment_record_api_pos_cash_rec_with_delete__payment_no__delete',
    description: `Delete a payment record`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'payment_no',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_cash_rec_with/location/:location',
    alias:
      'get_payments_by_location_api_pos_cash_rec_with_location__location__get',
    description: `Get payment records for a specific location`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'location',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'skip',
        type: 'Query',
        schema: z.number().int().optional().default(0),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional().default(100),
      },
    ],
    response: APIResponse_List_PosCashRecWithResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_cash_rec_with/update/:payment_no',
    alias:
      'update_payment_record_api_pos_cash_rec_with_update__payment_no__put',
    description: `Update a payment record`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosCashRecWithUpdate,
      },
      {
        name: 'payment_no',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosCashRecWithResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_customer_group',
    alias: 'get_all_customer_groups_api_pos_customer_group_get',
    requestFormat: 'json',
    response: APIResponse_List_PosCustomerGroupResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_customer_group/all',
    alias: 'get_all_customer_groups_api_pos_customer_group_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosCustomerGroupResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_customer/all',
    alias: 'load_customers_for_management_api_pos_customer_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosCustomerResponse__,
  },
  {
    method: 'post',
    path: '/api/pos_customer/create',
    alias: 'create_pos_customer_api_pos_customer_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosCustomerUpdateRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_customer/update/:cus_id',
    alias: 'update_pos_customer_api_pos_customer_update__cus_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosCustomerUpdateRequest,
      },
      {
        name: 'cus_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_customers',
    alias: 'load_customers_for_management_api_pos_customers_get',
    requestFormat: 'json',
    response: APIResponse_List_PosCustomerResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_customers/all',
    alias: 'load_customers_for_management_api_pos_customers_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosCustomerResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_gl_accounts',
    alias: 'get_all_accounts_api_pos_gl_accounts_get',
    requestFormat: 'json',
    response: APIResponse_list_PosGLAccountResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_gl_accounts/:account_id',
    alias: 'get_account_api_pos_gl_accounts__account_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosGLAccountResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_gl_accounts/all',
    alias: 'get_all_accounts_api_pos_gl_accounts_all_get',
    requestFormat: 'json',
    response: APIResponse_list_PosGLAccountResponse__,
  },
  {
    method: 'post',
    path: '/api/pos_gl_accounts/create',
    alias: 'create_account_api_pos_gl_accounts_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosGLAccountCreate,
      },
    ],
    response: APIResponse_PosGLAccountResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_gl_accounts/search',
    alias: 'search_accounts_api_pos_gl_accounts_search_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'q',
        type: 'Query',
        schema: z.string().optional().default(''),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(20).optional().default(10),
      },
    ],
    response: APIResponse_list_PosGLAccountSuggestionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_gl_accounts/update/:account_id',
    alias: 'update_account_api_pos_gl_accounts_update__account_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosGLAccountUpdate,
      },
      {
        name: 'account_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosGLAccountResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_invoice/:invoice_no',
    alias: 'get_invoice_detail_api_pos_invoice__invoice_no__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'invoice_no',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_PosInvoiceDetailResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_invoice/create',
    alias: 'create_invoice_api_pos_invoice_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosCreateInvoiceRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_invoice/recent',
    alias: 'get_recent_invoices_api_pos_invoice_recent_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional().default(10),
      },
      {
        name: 'search',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_list_PosRecentInvoiceResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_item_group',
    alias: 'get_all_item_groups_api_pos_item_group_get',
    description: `Get all item groups`,
    requestFormat: 'json',
    response: APIResponse_List_PosItemGroupResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_item_group/:group_id',
    alias: 'get_item_group_api_pos_item_group__group_id__get',
    description: `Get a specific item group by group_id`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'group_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosItemGroupResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_item_group/all',
    alias: 'get_all_item_groups_api_pos_item_group_all_get',
    description: `Get all item groups`,
    requestFormat: 'json',
    response: APIResponse_List_PosItemGroupResponse__,
  },
  {
    method: 'post',
    path: '/api/pos_item_group/create',
    alias: 'create_item_group_api_pos_item_group_create_post',
    description: `Create a new item group`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosItemGroupCreate,
      },
    ],
    response: APIResponse_PosItemGroupResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_item_group/update/:group_id',
    alias: 'update_item_group_api_pos_item_group_update__group_id__put',
    description: `Update an item group`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosItemGroupUpdate,
      },
      {
        name: 'group_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosItemGroupResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_item_resource',
    alias: 'get_all_item_resources_api_pos_item_resource_get',
    description: `Get all item resources`,
    requestFormat: 'json',
    response: APIResponse_List_PosItemResourceResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_item_resource/:resource_id',
    alias: 'get_item_resource_api_pos_item_resource__resource_id__get',
    description: `Get a specific item resource by resource_id`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosItemResourceResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_item_resource/all',
    alias: 'get_all_item_resources_api_pos_item_resource_all_get',
    description: `Get all item resources`,
    requestFormat: 'json',
    response: APIResponse_List_PosItemResourceResponse__,
  },
  {
    method: 'put',
    path: '/api/pos_item_resource/approve/:resource_id',
    alias:
      'approve_item_resource_api_pos_item_resource_approve__resource_id__put',
    description: `Approve a single pending resource`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosItemResourceResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_item_resource/by-item/:itemlots_code',
    alias:
      'get_resources_by_item_api_pos_item_resource_by_item__itemlots_code__get',
    description: `Get all resources for a specific item lot`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'itemlots_code',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_List_PosItemResourceResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_item_resource/clear-primary/:resource_id',
    alias:
      'clear_primary_item_resource_api_pos_item_resource_clear_primary__resource_id__put',
    description: `Unset an image resource as the main image representing the item`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosItemResourceResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_item_resource/create',
    alias: 'create_item_resource_api_pos_item_resource_create_post',
    description: `Create a new item resource`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosItemResourceCreate,
      },
    ],
    response: APIResponse_PosItemResourceResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/pos_item_resource/delete/:resource_id',
    alias:
      'delete_item_resource_api_pos_item_resource_delete__resource_id__delete',
    description: `Hard delete an item resource`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_item_resource/pending/all',
    alias: 'get_all_pending_resources_api_pos_item_resource_pending_all_get',
    description: `Get all pending resources across all items (for admin approval)`,
    requestFormat: 'json',
    response: APIResponse_List_PosItemResourceResponse__,
  },
  {
    method: 'put',
    path: '/api/pos_item_resource/set-primary/:resource_id',
    alias:
      'set_primary_item_resource_api_pos_item_resource_set_primary__resource_id__put',
    description: `Mark an image resource as the main image representing the item`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosItemResourceResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_item_resource/sync-all-pending',
    alias:
      'sync_all_pending_resources_api_pos_item_resource_sync_all_pending_post',
    description: `Send ALL pending (unapproved) resources across all items to the approval queue.
This does NOT approve the resources - it leaves is_approved&#x3D;0 so they only
become approved when an admin explicitly approves them from the Approval Panel.`,
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'put',
    path: '/api/pos_item_resource/sync/:itemlots_code',
    alias:
      'sync_item_resources_for_approval_api_pos_item_resource_sync__itemlots_code__put',
    description: `Send all pending (unapproved) resources for an item to the approval queue.
This does NOT approve the resources - it leaves is_approved&#x3D;0 so they only
become approved when an admin explicitly approves them from the Approval Panel.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'itemlots_code',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_item_resource/update/:resource_id',
    alias:
      'update_item_resource_api_pos_item_resource_update__resource_id__put',
    description: `Update an item resource`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosItemResourceUpdate,
      },
      {
        name: 'resource_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosItemResourceResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_item_resource/upload',
    alias: 'upload_resource_file_api_pos_item_resource_upload_post',
    description: `Upload an image or video file and return the saved path.
The path can then be used when creating/updating a pos_item_resource record.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: Body_upload_resource_file_api_pos_item_resource_upload_post,
      },
    ],
    response: APIResponse_str_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_item/all',
    alias: 'load_items_for_management_api_pos_item_all_get',
    description: `Load active items for ItemManagement page.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(100),
      },
      {
        name: 'search',
        type: 'Query',
        schema: search,
      },
      {
        name: 'status',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_List_PosItemResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_item/create',
    alias: 'create_pos_item_api_pos_item_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosItemUpdateRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_item/update/:item_code',
    alias: 'update_pos_item_api_pos_item_update__item_code__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosItemUpdateRequest,
      },
      {
        name: 'item_code',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_itemlots',
    alias: 'get_all_itemlots_api_pos_itemlots_get',
    requestFormat: 'json',
    response: APIResponse_List_PosItemLotsResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_itemlots/all',
    alias: 'get_all_itemlots_api_pos_itemlots_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosItemLotsResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_items',
    alias: 'load_items_for_management_api_pos_items_get',
    description: `Load active items for ItemManagement page.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(100),
      },
      {
        name: 'search',
        type: 'Query',
        schema: search,
      },
      {
        name: 'status',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_List_PosItemResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_items/all',
    alias: 'load_items_for_management_api_pos_items_all_get',
    description: `Load active items for ItemManagement page.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'page',
        type: 'Query',
        schema: z.number().int().gte(1).optional().default(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(100),
      },
      {
        name: 'search',
        type: 'Query',
        schema: search,
      },
      {
        name: 'status',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_List_PosItemResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_loc',
    alias: 'get_locations_api_pos_loc_get',
    requestFormat: 'json',
    response: APIResponse_List_Dict_str__Any___,
  },
  {
    method: 'get',
    path: '/api/pos_loc/:loc_code',
    alias: 'get_location_by_code_api_pos_loc__loc_code__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'loc_code',
        type: 'Path',
        schema: z.string().max(10),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_orders/:order_no',
    alias: 'update_pos_order_api_pos_orders__order_no__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosPlaceOrderRequest,
      },
      {
        name: 'order_no',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/pos_orders/:order_no',
    alias: 'delete_pos_order_api_pos_orders__order_no__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'order_no',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_orders/place',
    alias: 'place_pos_order_api_pos_orders_place_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosPlaceOrderRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_orders/recent',
    alias: 'get_recent_pos_orders_api_pos_orders_recent_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(20),
      },
    ],
    response: APIResponse_list_PosRecentOrderResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_paymode',
    alias: 'get_all_paymodes_api_pos_paymode_get',
    requestFormat: 'json',
    response: APIResponse_List_PosPayModeResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_paymode/all',
    alias: 'get_all_paymodes_api_pos_paymode_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosPayModeResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_paymodedet',
    alias: 'get_all_paymode_details_api_pos_paymodedet_get',
    requestFormat: 'json',
    response: APIResponse_List_PosPayModeDetResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_paymodedet/all',
    alias: 'get_all_paymode_details_api_pos_paymodedet_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosPayModeDetResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_pricechange/item-price-history/:item_code',
    alias:
      'get_item_price_history_api_pos_pricechange_item_price_history__item_code__get',
    description: `Get price change history for an item within the date window
defined by its item group&#x27;s group_prchadays setting.

The backend looks up:
    item_code → pos_itemlots.itemlots_group → pos_item_group.group_prchadays
    → pos_pricechange filtered by item_code + date window`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'item_code',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_returns/direct',
    alias: 'process_direct_return_api_pos_returns_direct_post',
    description: `Process a return without reference to original invoice:
1. User returns items directly from checkout
2. No validation against original invoice
3. Return rows use negative quantities
4. Useful for items without invoice reference or returns at time of purchase

This supports the non-referenced return flow where users:
- Return items directly during checkout
- No need to look up original invoice
- Quick refund processing`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DirectReturnRequest,
      },
    ],
    response: APIResponse_DirectReturnResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_returns/selective',
    alias: 'process_selective_return_api_pos_returns_selective_post',
    description: `Process a return with item selection:
1. Load original invoice items
2. Only selected items are converted to return rows
3. Non-selected items are NOT added to the transaction
4. Return rows use negative quantities in the same invoice table

This supports the redesigned return flow where users:
- Select which items to return (not auto-select all)
- Enter return quantity for each selected item
- Only pay for returned items`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SelectiveReturnRequest,
      },
    ],
    response: APIResponse_SelectiveReturnResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_setup',
    alias: 'get_setup_api_pos_setup_get',
    requestFormat: 'json',
    response: APIResponse_PosSetupResponse_,
  },
  {
    method: 'put',
    path: '/api/pos_setup/logo',
    alias: 'update_company_logo_api_pos_setup_logo_put',
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ logo: z.instanceof(File) }).passthrough(),
      },
    ],
    response: APIResponse_PosSetupResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_trasetup',
    alias: 'get_transaction_setups_api_pos_trasetup_get',
    requestFormat: 'json',
    response: APIResponse_List_Dict_str__Any___,
  },
  {
    method: 'get',
    path: '/api/pos_trasetup/:setup_no',
    alias: 'get_transaction_setup_api_pos_trasetup__setup_no__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_trasetup/:setup_no/sync-tables',
    alias:
      'sync_transaction_tables_for_setup_api_pos_trasetup__setup_no__sync_tables_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/pos_trasetup/sync-tables',
    alias: 'sync_transaction_tables_api_pos_trasetup_sync_tables_post',
    requestFormat: 'json',
    response: APIResponse_List_Dict_str__Any___,
  },
  {
    method: 'get',
    path: '/api/pos_tratype',
    alias: 'get_all_tra_types_api_pos_tratype_get',
    requestFormat: 'json',
    response: APIResponse_List_PosTraTypeResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_tratype/all',
    alias: 'get_all_tra_types_api_pos_tratype_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosTraTypeResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_vendor/all',
    alias: 'load_vendors_for_management_api_pos_vendor_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosVendorResponse__,
  },
  {
    method: 'post',
    path: '/api/pos_vendor/create',
    alias: 'create_pos_vendor_api_pos_vendor_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosVendorUpdateRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/pos_vendor/update/:ven_id',
    alias: 'update_pos_vendor_api_pos_vendor_update__ven_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosVendorUpdateRequest,
      },
      {
        name: 'ven_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/pos_vendors',
    alias: 'load_vendors_for_management_api_pos_vendors_get',
    requestFormat: 'json',
    response: APIResponse_List_PosVendorResponse__,
  },
  {
    method: 'get',
    path: '/api/pos_vendors/all',
    alias: 'load_vendors_for_management_api_pos_vendors_all_get',
    requestFormat: 'json',
    response: APIResponse_List_PosVendorResponse__,
  },
  {
    method: 'get',
    path: '/api/pos/cart',
    alias: 'get_cart_items_api_pos_cart_get',
    requestFormat: 'json',
    response: APIResponse,
  },
  {
    method: 'post',
    path: '/api/pos/cart',
    alias: 'add_cart_item_api_pos_cart_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: app__api__routes__pos_cart__CartItemRequest,
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/pos/cart',
    alias: 'clear_cart_api_pos_cart_delete',
    requestFormat: 'json',
    response: APIResponse,
  },
  {
    method: 'put',
    path: '/api/pos/cart/:item_id',
    alias: 'update_cart_item_api_pos_cart__item_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: CartItemUpdateRequest,
      },
      {
        name: 'item_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/pos/cart/:item_id',
    alias: 'remove_cart_item_api_pos_cart__item_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'item_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/profile/me',
    alias: 'get_profile_api_profile_me_get',
    requestFormat: 'json',
    response: APIResponse_AuthResponse_,
  },
  {
    method: 'get',
    path: '/api/promotion-types',
    alias: 'get_active_promotion_types_api_promotion_types_get',
    description: `Get all active promotion types`,
    requestFormat: 'json',
    response: APIResponse_List_PosPromoTypeListResponse__,
  },
  {
    method: 'get',
    path: '/api/promotions',
    alias: 'get_all_promotions_api_promotions_get',
    description: `Get all promotions`,
    requestFormat: 'json',
    response: APIResponse_List_PosPromoHedResponse__,
  },
  {
    method: 'post',
    path: '/api/promotions',
    alias: 'create_promotion_api_promotions_post',
    description: `Create a new promotion with offer criteria details`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosPromoHedCreate,
      },
    ],
    response: APIResponse_PosPromoHedResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/promotions/:promo_id',
    alias: 'get_promotion_api_promotions__promo_id__get',
    description: `Get a specific promotion by ID with its details`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'promo_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosPromoHedResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/promotions/:promo_id',
    alias: 'update_promotion_api_promotions__promo_id__put',
    description: `Update a promotion and its offer criteria details`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosPromoHedUpdate,
      },
      {
        name: 'promo_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosPromoHedResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/promotions/:promo_id',
    alias: 'delete_promotion_api_promotions__promo_id__delete',
    description: `Delete a promotion and its details (cascade)`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'promo_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/promotions/:promo_id/approve',
    alias: 'approve_promotion_api_promotions__promo_id__approve_put',
    description: `Approve a pending promotion, making it eligible for use at checkout`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'promo_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosPromoHedResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/promotions/:promo_id/reject',
    alias: 'reject_promotion_api_promotions__promo_id__reject_put',
    description: `Reject a pending promotion`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PosPromoRejectRequest,
      },
      {
        name: 'promo_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_PosPromoHedResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/promotions/pending',
    alias: 'get_pending_promotions_api_promotions_pending_get',
    description: `Get all promotions awaiting approval`,
    requestFormat: 'json',
    response: APIResponse_List_PosPromoHedResponse__,
  },
  {
    method: 'post',
    path: '/api/promotions/select',
    alias: 'select_promotion_api_promotions_select_post',
    description: `Automatically select the best matching promotion based on cart context.
Returns the selected promotion or null if no match found.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: PromotionSelectionRequest,
      },
    ],
    response: APIResponse_PromotionSelectionListResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/report/:report_id',
    alias: 'delete_report_api_report__report_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'report_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/report/:report_id/languages',
    alias: 'report_uploaded_languages_api_report__report_id__languages_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'report_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/report/download_report',
    alias: 'download_report_api_report_download_report_post',
    description: `Generate report output (PDF or HTML) for a saved template.

Request must include &#x60;report_id&#x60;, &#x60;file_format&#x60; (&#x27;pdf&#x27;|&#x27;html&#x27;) and optional &#x60;parameters&#x60; dict and &#x60;language_code&#x60;.
Report generation is delegated to external Jasper Reports Service.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/report/list_reports',
    alias: 'list_reports_api_report_list_reports_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/report/list_reports/current_user',
    alias:
      'list_reports_for_current_user_api_report_list_reports_current_user_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/report/parameters_types',
    alias: 'parameters_types_api_report_parameters_types_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/report/template_categories',
    alias: 'template_categories_api_report_template_categories_get',
    requestFormat: 'json',
    response: z.unknown(),
  },
  {
    method: 'get',
    path: '/api/report/template/:template_id',
    alias: 'get_template_api_report_template__template_id__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'template_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/report/upload_jrxml/:report_uuid',
    alias: 'upload_jrxml_with_uuid_api_report_upload_jrxml__report_uuid__post',
    description: `Upload a JRXML file associated with a report UUID and language code.

Uploads file to external Jasper Reports Service with filename format:
{report_uuid}_{language_code}.jrxml and updates database metadata.`,
    requestFormat: 'form-data',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ file: z.instanceof(File) }).passthrough(),
      },
      {
        name: 'report_uuid',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'language_code',
        type: 'Query',
        schema: z.string().optional().default('en'),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/report/upload_report',
    alias: 'upload_report_api_report_upload_report_post',
    description: `Create or update a report template record.

Expects JSON body compatible with &#x60;UploadReportRequest&#x60;.
If &#x60;id&#x60; is provided the record will be updated, otherwise a new report UUID record will be created.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
    ],
    response: APIResponse,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/sales_reps',
    alias: 'get_all_sales_reps_api_sales_reps_get',
    description: `Get all users with SALES_REP role`,
    requestFormat: 'json',
    response: APIResponse_List_Dict_str__Any___,
  },
  {
    method: 'get',
    path: '/api/signon/check_status',
    alias: 'check_user_signon_status_api_signon_check_status_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/signon/check_status/',
    alias: 'check_user_signon_status_api_signon_check_status__get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/signon/server_datetime',
    alias: 'get_server_datetime_route_api_signon_server_datetime_get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'get',
    path: '/api/signon/server_datetime/',
    alias: 'get_server_datetime_route_api_signon_server_datetime__get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'put',
    path: '/api/signon/signoff',
    alias: 'signoff_user_api_signon_signoff_put',
    description: `Sign off a user in it_user_master:
- signon &#x3D; 0
- signoff &#x3D; 1
- signoffdate/signofftime &#x3D; NOW()`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: signoff_user_api_api_signon_signoff__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/signon/signoff/',
    alias: 'signoff_user_api_signon_signoff__put',
    description: `Sign off a user in it_user_master:
- signon &#x3D; 0
- signoff &#x3D; 1
- signoffdate/signofftime &#x3D; NOW()`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: signoff_user_api_api_signon_signoff__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/signon/user_records',
    alias: 'get_user_signon_records_api_signon_user_records_get',
    description: `Retrieve sign-on records from it_user_master table for a given user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/signon/user_records',
    alias: 'update_user_signon_records_api_signon_user_records_put',
    description: `Update sign-on records in it_user_master by username.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          update_user_signon_records_api_api_signon_user_records__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signon',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'signondate',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signontime',
        type: 'Query',
        schema: search,
      },
      {
        name: 'station_id',
        type: 'Query',
        schema: search,
      },
      {
        name: 'stationid',
        type: 'Query',
        schema: search,
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/signon/user_records/',
    alias: 'get_user_signon_records_api_signon_user_records__get',
    description: `Retrieve sign-on records from it_user_master table for a given user.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_name',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'userName',
        type: 'Query',
        schema: z.string().min(1).optional(),
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/signon/user_records/',
    alias: 'update_user_signon_records_api_signon_user_records__put',
    description: `Update sign-on records in it_user_master by username.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema:
          update_user_signon_records_api_api_signon_user_records__put_Body,
      },
      {
        name: 'user_name',
        type: 'Query',
        schema: search,
      },
      {
        name: 'userName',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signon',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'signondate',
        type: 'Query',
        schema: search,
      },
      {
        name: 'signontime',
        type: 'Query',
        schema: search,
      },
      {
        name: 'station_id',
        type: 'Query',
        schema: search,
      },
      {
        name: 'stationid',
        type: 'Query',
        schema: search,
      },
      {
        name: 'floatamt',
        type: 'Query',
        schema: floatamt,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/system_permissions/all',
    alias: 'get_all_system_permissions_api_system_permissions_all_get',
    requestFormat: 'json',
    response: APIResponse_list_SystemPermissionResponse__,
  },
  {
    method: 'get',
    path: '/api/transaction-reports/available-types',
    alias:
      'get_available_report_types_api_transaction_reports_available_types_get',
    description: `Get list of transaction report types from pos_trasetup.`,
    requestFormat: 'json',
    response: APIResponse_List_Dict_str__Any___,
  },
  {
    method: 'post',
    path: '/api/transaction-reports/generate',
    alias: 'generate_transaction_report_api_transaction_reports_generate_post',
    description: `Generate a printable report for a transaction document using Jasper Studio templates

Returns PDF file for download`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({}).partial().passthrough(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transaction-reports/preview/:setup_no/:doc_no',
    alias:
      'get_transaction_report_preview_api_transaction_reports_preview__setup_no___doc_no__get',
    description: `Get preview data for a transaction report (header and detail lines)

Useful for showing report contents before printing`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
      {
        name: 'doc_no',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/:setup_no/details/:doc_no',
    alias:
      'get_document_details_api_transactions__setup_no__details__doc_no__get',
    description: `Get all detail lines for a document.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
      {
        name: 'doc_no',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_List_Dict_str__Any___,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transactions/:setup_no/document',
    alias: 'create_document_api_transactions__setup_no__document_post',
    description: `Create a new transaction document with header and detail lines.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: DocumentRequest,
      },
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/:setup_no/documents/search',
    alias: 'search_documents_api_transactions__setup_no__documents_search_get',
    description: `Search older transaction documents for recall.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
      {
        name: 'q',
        type: 'Query',
        schema: search,
      },
      {
        name: 'date_from',
        type: 'Query',
        schema: search,
      },
      {
        name: 'date_to',
        type: 'Query',
        schema: search,
      },
      {
        name: 'status',
        type: 'Query',
        schema: z
          .enum(['all', 'temporary', 'processed'])
          .optional()
          .default('all'),
      },
      {
        name: 'amount_min',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'amount_max',
        type: 'Query',
        schema: floatamt,
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).lte(100).optional().default(25),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional().default(0),
      },
    ],
    response: APIResponse_List_Dict_str__Any___,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/:setup_no/header/:doc_no',
    alias:
      'get_document_header_api_transactions__setup_no__header__doc_no__get',
    description: `Get a specific document header by document number.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
      {
        name: 'doc_no',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/:setup_no/recent',
    alias: 'get_recent_documents_api_transactions__setup_no__recent_get',
    description: `Get recent documents for a transaction setup.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().optional().default(10),
      },
    ],
    response: APIResponse_List_Dict_str__Any___,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/:setup_no/setup',
    alias: 'get_transaction_setup_api_transactions__setup_no__setup_get',
    description: `Get a specific transaction setup configuration.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transactions/:setup_no/stage-line',
    alias: 'stage_temp_line_api_transactions__setup_no__stage_line_post',
    description: `Stage a single line into local tra_tmp using station_id + trdet_type DocNo.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: StageLineRequest,
      },
      {
        name: 'setup_no',
        type: 'Path',
        schema: z.number(),
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/account-entries',
    alias: 'get_account_entries_api_transactions_account_entries_get',
    requestFormat: 'json',
    parameters: [
      {
        name: 'membercode',
        type: 'Query',
        schema: z.string().min(1),
      },
      {
        name: 'party_type',
        type: 'Query',
        schema: z.string().min(1),
      },
    ],
    response: APIResponse_List_Dict_str__Any___,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transactions/account-entries/save',
    alias:
      'save_account_entries_payment_api_transactions_account_entries_save_post',
    description: `Save allocated account entries as an accounts-payable payment.

Inserts pos_transaction rows with status&#x3D;&#x27;ACCPAY&#x27;, a pos_accpayhed header,
pos_accpaydet detail rows, and a pos_accpaypay payment row.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AccountPaymentRequest,
      },
    ],
    response: APIResponse_dict_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/transactions/calculate-discount',
    alias: 'calculate_line_discount_api_transactions_calculate_discount_post',
    description: `Calculate discount amount and line total.

Given quantity, unit price, and discount percentage, returns:
- discount_amount: Absolute discount amount
- line_amount: Net amount after discount
- discount_percentage: The discount percentage used`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: LineDiscountRequest,
      },
    ],
    response: APIResponse_Dict_str__Any__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/setups',
    alias: 'get_all_transaction_setups_api_transactions_setups_get',
    requestFormat: 'json',
    response: APIResponse_List_Dict_str__Any___,
  },
  {
    method: 'get',
    path: '/api/transactions/temp-documents/all',
    alias: 'get_all_temp_documents_api_transactions_temp_documents_all_get',
    description: `Get all temporary documents from local tra_tmp for UI refresh restoration.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'transaction_type',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_List_Dict_str__Any___,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/transactions/temp-lines/:temp_no',
    alias:
      'get_temp_lines_for_document_api_transactions_temp_lines__temp_no__get',
    description: `Get all temporary lines for a document (used for UI refresh restoration).`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'temp_no',
        type: 'Path',
        schema: z.string(),
      },
      {
        name: 'transaction_type',
        type: 'Query',
        schema: search,
      },
    ],
    response: APIResponse_List_Dict_str__Any___,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/updated-version',
    alias: 'get_updated_version_api_updated_version_get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
  {
    method: 'post',
    path: '/api/users/:user_id/activate',
    alias: 'activate_user_api_users__user_id__activate_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/:user_id/deactivate',
    alias: 'deactivate_user_api_users__user_id__deactivate_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/menu_list',
    alias: 'get_user_menu_list_api_users__user_id__menu_list_get',
    description: `Get all menus assigned to a user with full details.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_UserMenuResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/menus',
    alias: 'get_user_menus_api_users__user_id__menus_get',
    description: `Get all menu IDs for a specific user where status&#x3D;True`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_int__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/permission_list',
    alias: 'get_user_permission_list_api_users__user_id__permission_list_get',
    description: `Get all permissions assigned to a user with full details.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_UserPermissionResponse__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/permissions',
    alias: 'get_user_permissions_api_users__user_id__permissions_get',
    description: `Get all permission IDs for a specific user where status&#x3D;True`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_int__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/:user_id/reports',
    alias: 'get_user_reports_api_users__user_id__reports_get',
    description: `Get all report IDs for a specific user where status&#x3D;1`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_list_int__,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/:user_id/reset_password',
    alias: 'reset_user_password_api_users__user_id__reset_password_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_str_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'post',
    path: '/api/users/:user_id/send_offline',
    alias: 'send_user_offline_api_users__user_id__send_offline_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_bool_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/all',
    alias: 'get_all_users_api_users_all_get',
    requestFormat: 'json',
    response: APIResponse_list_UserWithActiveStatusResponse__,
  },
  {
    method: 'post',
    path: '/api/users/create',
    alias: 'create_new_user_api_users_create_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserCreateRequest,
      },
    ],
    response: APIResponse_UserCreateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'delete',
    path: '/api/users/delete_user/:user_id',
    alias: 'delete_user_api_users_delete_user__user_id__delete',
    requestFormat: 'json',
    parameters: [
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/api/users/favourite_menu_list',
    alias: 'get_user_favourite_menu_list_api_users_favourite_menu_list_get',
    description: `Get all menus assigned to a user with full details.`,
    requestFormat: 'json',
    response: APIResponse_list_UserMenuResponse__,
  },
  {
    method: 'get',
    path: '/api/users/groups/all',
    alias: 'get_all_user_groups_api_users_groups_all_get',
    requestFormat: 'json',
    response: APIResponse_list_UserGroupsResponse__,
  },
  {
    method: 'get',
    path: '/api/users/menu_list',
    alias: 'get_current_user_menu_list_api_users_menu_list_get',
    description: `Get all menus for the current user with full details.`,
    requestFormat: 'json',
    response: APIResponse_list_UserMenuResponse__,
  },
  {
    method: 'get',
    path: '/api/users/permission_list',
    alias: 'get_current_user_permission_list_api_users_permission_list_get',
    description: `Get all permissions for the current user with full details.`,
    requestFormat: 'json',
    response: APIResponse_list_UserPermissionResponse__,
  },
  {
    method: 'post',
    path: '/api/users/update_favourite_menu',
    alias: 'update_user_favourite_menu_api_users_update_favourite_menu_post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UpdateFavoriteMenuRequest,
      },
    ],
    response: APIResponse_UpdateFavouriteMenuResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'put',
    path: '/api/users/update/:user_id',
    alias: 'update_user_api_users_update__user_id__put',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: UserCreateRequest,
      },
      {
        name: 'user_id',
        type: 'Path',
        schema: z.number().int(),
      },
    ],
    response: APIResponse_UserCreateResponse_,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: 'get',
    path: '/health',
    alias: 'health_check_health_get',
    requestFormat: 'json',
    response: APIResponse_dict_,
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
